import type { FileDiff } from "@mux/sdk/v2/client"
import { retry } from "@/utils/retry"
import type { SessionDiffSummary } from "../types"
import type { SessionDiffClient } from "./contracts"

const runInflight = (map: Map<string, Promise<void>>, key: string, task: () => Promise<void>) => {
  const current = map.get(key)
  if (current) return current
  const request = task().finally(() => {
    if (map.get(key) === request) map.delete(key)
  })
  map.set(key, request)
  return request
}

export function createSessionAuxiliaryController(input: {
  diffClientFor(directory: string): SessionDiffClient
  tracked(directory: string, sessionID: string): boolean
  touch(directory: string, sessionID: string): void
  getDiff(sessionID: string): FileDiff[] | undefined
  setDiff(sessionID: string, diff: FileDiff[]): void
  getDiffSummary(sessionID: string): SessionDiffSummary | undefined
  setDiffSummary(sessionID: string, summary: SessionDiffSummary): void
}) {
  const diffInflight = new Map<string, Promise<void>>()
  const requests = new Map<string, AbortController>()
  const clear = (directory: string, sessionIDs: Iterable<string>) => {
    for (const sessionID of sessionIDs) {
      const id = key(directory, sessionID)
      requests.get(id)?.abort()
      requests.delete(id)
      diffInflight.delete(id)
    }
  }
  let disposed = false
  const key = (directory: string, sessionID: string) => `${directory}\n${sessionID}`

  const loadDiff = async (directory: string, sessionID: string, signal: AbortSignal): Promise<void> => {
    const requestedRevision = input.getDiffSummary(sessionID)?.revision
    const client = input.diffClientFor(directory)
    const result = await retry(() => client.session.diff({ sessionID }, { signal }), { signal })
    if (disposed || signal.aborted || !input.tracked(directory, sessionID)) return
    if (input.getDiffSummary(sessionID)?.revision !== requestedRevision) {
      await loadDiff(directory, sessionID, signal)
      return
    }
    const diff = result.data ?? []
    input.setDiff(sessionID, diff)
    input.setDiffSummary(sessionID, {
      revision: requestedRevision ?? Date.now(),
      files: diff.length,
      additions: diff.reduce((sum, item) => sum + item.additions, 0),
      deletions: diff.reduce((sum, item) => sum + item.deletions, 0),
    })
  }

  return {
    clear,
    diff(directory: string, sessionID: string, options?: { force?: boolean }) {
      if (disposed) return Promise.resolve()
      input.touch(directory, sessionID)
      if (input.getDiff(sessionID) !== undefined && !options?.force) return Promise.resolve()
      const id = key(directory, sessionID)
      return runInflight(diffInflight, id, () => {
        const owner = new AbortController()
        requests.set(id, owner)
        return loadDiff(directory, sessionID, owner.signal).catch((error) => {
          if (!owner.signal.aborted) throw error
        }).finally(() => { if (requests.get(id) === owner) requests.delete(id) })
      })
    },
    dispose() {
      disposed = true
      for (const owner of requests.values()) owner.abort()
      requests.clear()
      diffInflight.clear()
    },
  }
}
